import * as Sentry from '@sentry/react-router';
import { setDefaultOptions } from 'date-fns';
import { z } from 'zod/v4';
import { getCookie, setCookie } from '~/utils/cookies';
import { getDateFnsLocale, loadLocale } from './locale-catalogue';
import type { LocaleCode } from './locales-config';
import { DEFAULT_LOCALE, localeCodeEnum } from './locales-config';

/*
 * The single source of truth for the app's locale. It only ever activates a locale whose messages have loaded, so
 * messages, date-fns and `Intl` formatting agree. The one exception is the hydration render, which React renders in
 * the default locale to match the prerendered shell before re-rendering in the active one.
 */

export const LOCALE_COOKIE = 'site_language';

// Long enough for a slow connection, short enough that a stalled request can't keep the app on its loading screen
export const LOCALE_STARTUP_TIMEOUT_MS = 8_000;

export const localeSwitchResultEnum = z.enum(['switched', 'failed', 'superseded']);
export type LocaleSwitchResult = z.infer<typeof localeSwitchResultEnum>;

let activeLocale: LocaleCode = DEFAULT_LOCALE;
let latestRequestedLocale: LocaleCode | undefined;
const listeners = new Set<() => void>();

function activate(locale: LocaleCode) {
  activeLocale = locale;
  setDefaultOptions({ locale: getDateFnsLocale(locale) });
  listeners.forEach((listener) => listener());
}

// date-fns otherwise defaults to en-US until a locale is activated
setDefaultOptions({ locale: getDateFnsLocale(activeLocale) });

export const getActiveLocale = () => activeLocale;

export function subscribeToActiveLocale(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getPreferredLocale(): LocaleCode {
  const result = localeCodeEnum.safeParse(getCookie(LOCALE_COOKIE));
  return result.success ? result.data : DEFAULT_LOCALE;
}

function loadWithinStartupTimeout(locale: LocaleCode): Promise<LocaleCode> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeout = new Promise<LocaleCode>((resolve) => {
    timeoutId = setTimeout(() => {
      Sentry.captureMessage(`Timed out loading locale "${locale}", starting with ${DEFAULT_LOCALE}`, 'warning');
      resolve(DEFAULT_LOCALE);
    }, LOCALE_STARTUP_TIMEOUT_MS);
  });

  return Promise.race([loadLocale(locale), timeout]).finally(() => clearTimeout(timeoutId));
}

/**
 * Activates the user's preferred locale before the first render. Never rejects, so it can't block start-up. The
 * cookie keeps the preference when loading fails, so the next page load retries it.
 */
export async function initLocale(): Promise<LocaleCode> {
  const locale = await loadWithinStartupTimeout(getPreferredLocale());
  activate(locale);
  return locale;
}

/**
 * Loads and then activates a locale, saving it as the user's preference. The current locale is kept when loading fails
 * (it beats the fallback) or when a newer switch supersedes this one.
 */
export async function switchLocale(locale: LocaleCode): Promise<LocaleSwitchResult> {
  latestRequestedLocale = locale;
  const loadedLocale = await loadLocale(locale);

  if (latestRequestedLocale !== locale) {
    return localeSwitchResultEnum.enum.superseded;
  }

  if (loadedLocale !== locale) {
    return localeSwitchResultEnum.enum.failed;
  }

  activate(locale);
  setCookie(LOCALE_COOKIE, locale);
  return localeSwitchResultEnum.enum.switched;
}
